/**
 * PromptDialog · reemplaza `window.prompt()` con UI MANAXER.
 *
 * Uso vía hook `usePrompt()`:
 *   const prompt = usePrompt();
 *   const url = await prompt({ message: 'URL del link', defaultValue: 'https://', placeholder: '...' });
 *   if (url === null) return; // canceló
 *   // ... usar url
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Pencil } from 'lucide-react';
import { Modal } from './Modal';
import { Button } from './Button';

export interface PromptOptions {
  title?: string;
  message: string;
  defaultValue?: string;
  placeholder?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Validación inline. Retorna mensaje de error o null si OK. */
  validate?: (value: string) => string | null;
  /** Multilínea (textarea en lugar de input). */
  multiline?: boolean;
}

interface PromptContextValue {
  prompt: (opts: PromptOptions) => Promise<string | null>;
}

const PromptContext = createContext<PromptContextValue | null>(null);

export function PromptProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<PromptOptions | null>(null);
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const resolverRef = useRef<((v: string | null) => void) | null>(null);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  const prompt = useCallback((opts: PromptOptions) => {
    return new Promise<string | null>((resolve) => {
      resolverRef.current = resolve;
      setValue(opts.defaultValue ?? '');
      setError(null);
      setState(opts);
    });
  }, []);

  useEffect(() => {
    if (state) {
      // Focus al input al abrir (siguiente tick para que esté montado)
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [state]);

  function close(out: string | null) {
    setState(null);
    if (resolverRef.current) {
      resolverRef.current(out);
      resolverRef.current = null;
    }
  }

  function onSubmit() {
    if (state?.validate) {
      const err = state.validate(value);
      if (err) {
        setError(err);
        return;
      }
    }
    close(value);
  }

  function onKey(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !state?.multiline) {
      e.preventDefault();
      onSubmit();
    }
  }

  return (
    <PromptContext.Provider value={{ prompt }}>
      {children}
      <Modal
        open={!!state}
        onClose={() => close(null)}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => close(null)}>
              {state?.cancelLabel ?? 'Cancelar'}
            </Button>
            <Button onClick={onSubmit}>{state?.confirmLabel ?? 'Aceptar'}</Button>
          </>
        }
      >
        <div className="flex items-start gap-3">
          <div className="grid h-10 w-10 flex-none place-items-center rounded-md bg-blue-10/40">
            <Pencil size={18} className="text-blue" />
          </div>
          <div className="min-w-0 flex-1 space-y-3">
            {state?.title && (
              <h3 className="text-base font-extrabold tracking-tight text-neutral-ink">
                {state.title}
              </h3>
            )}
            <p className="text-sm leading-relaxed text-neutral-muted">{state?.message}</p>
            {state?.multiline ? (
              <textarea
                ref={(el) => (inputRef.current = el)}
                value={value}
                onChange={(e) => {
                  setValue(e.target.value);
                  setError(null);
                }}
                placeholder={state.placeholder}
                rows={4}
                className="w-full rounded-md border border-surface-line bg-white px-3 py-2 text-sm text-neutral-ink focus:border-blue focus:outline-none focus:ring-2 focus:ring-blue-30"
              />
            ) : (
              <input
                ref={(el) => (inputRef.current = el)}
                value={value}
                onChange={(e) => {
                  setValue(e.target.value);
                  setError(null);
                }}
                onKeyDown={onKey}
                placeholder={state?.placeholder}
                className="w-full rounded-md border border-surface-line bg-white px-3 py-2 text-sm text-neutral-ink focus:border-blue focus:outline-none focus:ring-2 focus:ring-blue-30"
              />
            )}
            {error && <p className="text-xs text-status-danger">{error}</p>}
          </div>
        </div>
      </Modal>
    </PromptContext.Provider>
  );
}

export function usePrompt(): (opts: PromptOptions) => Promise<string | null> {
  const ctx = useContext(PromptContext);
  if (!ctx) {
    throw new Error('usePrompt() requires <PromptProvider> in the tree');
  }
  return ctx.prompt;
}
